import React, { forwardRef, useImperativeHandle, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  ContentCopy as CopyIcon, 
  Edit as EditIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import citiesData from '../address_data/cities.json';
import districtsData from '../address_data/districts.json';
import communesData from '../address_data/communes.json';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

interface City {
  id: number;
  name: string;
  name_transliteration: string;
  alias: string;
  is_active: boolean;
  is_big_city: boolean;
  regional_id: number;
  country_id: number;
  number: number;
}

interface District {
  id: number;
  city_id: number;
  name: string;
  name_transliteration: string;
  number: number;
  alias: string;
  country_id: number;
}

interface Commune {
  id: number;
  name: string;
  name_transliteration: string;
  city: string | null;
  district: string | null;
  zip_code: string | null;
  district_id: number;
  city_id: number;
}

interface NormalizedAddress {
  originalAddress: string;
  city: string;
  district: string;
  commune: string;
  newCity?: string;
  newCommune?: string;
}

interface AddressTableProps {
  addresses: NormalizedAddress[];
  type: 'original' | 'normalized';
  onAddressUpdate?: (index: number, updatedAddress: NormalizedAddress) => void;
  onExport?: () => void;
  isNormalizing?: boolean;
  isNormalizedDone?: boolean;
}

export interface AddressTableRef {
  fetchAddresses: () => void;
}

// Type assertions for imported JSON data
const typedCitiesData = citiesData as City[];
const typedDistrictsData = districtsData as District[];
const typedCommunesData = communesData as unknown as Commune[];

interface EditDialogProps {
  open: boolean;
  address: NormalizedAddress;
  onClose: () => void;
  onSave: (updatedAddress: NormalizedAddress) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ open, address, onClose, onSave }) => {
  const [selectedCity, setSelectedCity] = useState<number | ''>('');
  const [selectedDistrict, setSelectedDistrict] = useState<number | ''>('');
  const [selectedCommune, setSelectedCommune] = useState<number | ''>('');

  // Find initial values when dialog opens
  React.useEffect(() => {
    if (open && address) {
      const city = typedCitiesData.find(c => c.name === address.city);
      setSelectedCity(city?.id || '');

      const district = typedDistrictsData.find(d => d.name === address.district && d.city_id === city?.id);
      setSelectedDistrict(district?.id || '');

      const commune = typedCommunesData.find(c => c.name === address.commune && c.district_id === district?.id);
      setSelectedCommune(commune?.id || '');
    }
  }, [open, address]);

  // Filter districts based on selected city
  const filteredDistricts = typedDistrictsData.filter(
    district => district.city_id === selectedCity
  );

  // Filter communes based on selected district
  const filteredCommunes = typedCommunesData.filter(
    commune => commune.district_id === selectedDistrict
  );

  const handleCityChange = (event: any) => {
    const cityId = event.target.value;
    setSelectedCity(cityId);
    setSelectedDistrict('');
    setSelectedCommune('');
  };

  const handleDistrictChange = (event: any) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedCommune('');
  };

  const handleCommuneChange = (event: any) => {
    setSelectedCommune(event.target.value);
  };

  const handleSave = () => {
    const selectedCityObj = typedCitiesData.find(c => c.id === selectedCity);
    const selectedDistrictObj = typedDistrictsData.find(d => d.id === selectedDistrict);
    const selectedCommuneObj = typedCommunesData.find(c => c.id === selectedCommune);

    onSave({
      ...address,
      city: selectedCityObj?.name || '',
      district: selectedDistrictObj?.name || '',
      commune: selectedCommuneObj?.name || ''
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Chỉnh sửa địa chỉ</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Tỉnh/Thành phố</InputLabel>
            <Select
              value={selectedCity}
              label="Tỉnh/Thành phố"
              onChange={handleCityChange}
            >
              {typedCitiesData.map(city => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Quận/Huyện</InputLabel>
            <Select
              value={selectedDistrict}
              label="Quận/Huyện"
              onChange={handleDistrictChange}
              disabled={!selectedCity}
            >
              {filteredDistricts.map(district => (
                <MenuItem key={district.id} value={district.id}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Phường/Xã</InputLabel>
            <Select
              value={selectedCommune}
              label="Phường/Xã"
              onChange={handleCommuneChange}
              disabled={!selectedDistrict}
            >
              {filteredCommunes.map(commune => (
                <MenuItem key={commune.id} value={commune.id}>
                  {commune.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button onClick={handleSave} variant="contained">
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AddressTable = forwardRef<AddressTableRef, AddressTableProps>(
  ({ addresses, type, onAddressUpdate, onExport, isNormalizing, isNormalizedDone }, ref) => {
    const theme = useTheme();
    const [copyNotification, setCopyNotification] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<{ index: number; address: NormalizedAddress } | null>(null);
    const [retryLoading, setRetryLoading] = useState<{ [key: number]: boolean }>({});
    const POLLING_INTERVAL = 2000;

    useImperativeHandle(ref, () => ({
      fetchAddresses: () => {
        // Implement if needed
      }
    }));

    const handleCopyColumn = (columnData: string[]) => {
      const textToCopy = columnData.join('\n');
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopyNotification({ open: true, message: 'Đã sao chép vào clipboard!' });
      }).catch(() => {
        setCopyNotification({ open: true, message: 'Không thể sao chép!' });
      });
    };

    const handleCloseNotification = () => {
      setCopyNotification({ ...copyNotification, open: false });
    };

    const handleEdit = (index: number, address: NormalizedAddress) => {
      setSelectedAddress({ index, address });
      setEditDialogOpen(true);
    };

    const handleSaveEdit = (updatedAddress: NormalizedAddress) => {
      if (selectedAddress && onAddressUpdate) {
        onAddressUpdate(selectedAddress.index, updatedAddress);
      }
      setEditDialogOpen(false);
      setSelectedAddress(null);
    };

    const handleRetry = async (index: number, address: NormalizedAddress) => {
      setRetryLoading(prev => ({ ...prev, [index]: true }));
      try {
        // Check if user has enough gold coins for retry (ChatGPT)
        const userResponse = await axios.get(API_ENDPOINTS.AUTH.COINS);
        const userGoldCoins = userResponse.data.goldCoins;
        if (userGoldCoins <= 0) {
          alert('Bạn đã hết coin vàng để thực hiện retry. Vui lòng nạp thêm coins.');
          return;
        }
        
        // Gửi originalAddress lên API retry-normalize (dạng mảng)
        const response = await axios.post(API_ENDPOINTS.UPLOAD.RETRY_NORMALIZE, {
          addresses: [address.originalAddress]
        });
        const queueId = response.data.queueId;
        if (!queueId) throw new Error('Không nhận được queueId');

        // Poll kết quả
        let done = false;
        let result: any = null;
        while (!done) {
          await new Promise(res => setTimeout(res, POLLING_INTERVAL));
          const statusRes = await axios.get(API_ENDPOINTS.UPLOAD.STATUS(queueId));
          if (statusRes.data.status === 'completed' && Array.isArray(statusRes.data.result)) {
            result = statusRes.data.result[0];
            done = true;
          } else if (statusRes.data.status === 'failed') {
            throw new Error('Chuẩn hóa thất bại');
          }
        }
        if (result) {
          // Cập nhật lại row
          if (onAddressUpdate) {
            onAddressUpdate(index, {
              ...address,
              ...result
            });
          }
        }
      } catch (err: any) {
        alert(err.message || 'Có lỗi khi retry chuẩn hóa');
      } finally {
        setRetryLoading(prev => ({ ...prev, [index]: false }));
      }
    };

    // Function to check if an address has missing information
    const hasMissingInfo = (address: NormalizedAddress) => {
      return !address.city || !address.district || !address.commune;
    };

    // Function to get missing fields for an address
    const getMissingFields = (address: NormalizedAddress) => {
      const missing = [];
      if (!address.city) missing.push('Tỉnh/Thành phố');
      if (!address.district) missing.push('Quận/Huyện');
      if (!address.commune) missing.push('Phường/Xã');
      return missing;
    };

    // Calculate total rows with missing information
    const totalMissingRows = (isNormalizedDone && type === 'normalized') 
      ? addresses.filter(addr => hasMissingInfo(addr)).length 
      : 0;

    if (!addresses || addresses.length === 0) {
      return (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: '200px',
            color: 'text.secondary'
          }}>
            <Typography>
              {type === 'normalized' ? 'Chưa có kết quả chuẩn hóa' : 'Chưa có địa chỉ'}
            </Typography>
          </Box>
        </Paper>
      );
    }

    // Prepare column data for copying
    const originalData = addresses.map(addr => addr.originalAddress);
    const cityData = addresses.map(addr => addr.city || '-');
    const districtData = addresses.map(addr => addr.district || '-');
    const communeData = addresses.map(addr => addr.commune || '-');
    const newCityData = addresses.map(addr => addr.newCity || '-');
    const newCommuneData = addresses.map(addr => addr.newCommune || '-');
    const deliveryAreaData = addresses.map(addr => (addr.city && addr.district ? `${addr.city} - ${addr.district}` : '-'));

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            {type === 'normalized' ? 'Địa chỉ đã chuẩn hóa' : 'Địa chỉ gốc'}
            {isNormalizedDone && type === 'normalized' && totalMissingRows > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${totalMissingRows} dòng thiếu thông tin`}
                color="warning"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          {type === 'normalized' && onExport && (
            <Button
              variant="contained"
              color="primary"
              onClick={onExport}
              disabled={isNormalizing || !addresses.length}
              startIcon={<DownloadIcon />}
              sx={{ ml: 'auto' }}
            >
              Xuất Excel
            </Button>
          )}
        </Box>
        <TableContainer component={Paper} sx={{ boxShadow: theme.shadows[2], borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.main, 
                  width: '40px', 
                  padding: '4px 8px',
                  textAlign: 'center'
                }}>
                  STT
                </TableCell>
                {type === 'original' ? (
                  <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Địa chỉ gốc
                      <Tooltip title="Sao chép cột">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyColumn(originalData)}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Địa chỉ gốc
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(originalData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Thành phố/Tỉnh
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(cityData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Quận/Huyện
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(districtData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Phường/Xã
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(communeData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Tỉnh/Thành phố mới
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(newCityData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Phường/Xã mới
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(newCommuneData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Khu vực giao hàng
                        <Tooltip title="Sao chép cột">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyColumn(deliveryAreaData)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      Thao tác
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {addresses.map((address, index) => {
                const isMissingInfo = type === 'normalized' && hasMissingInfo(address);
                const missingFields = isMissingInfo ? getMissingFields(address) : [];
                
                return (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                      '& td': { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
                      ...(isNormalizedDone && isMissingInfo && {
                        backgroundColor: alpha(theme.palette.warning.light, 0.1),
                        '&:hover': { backgroundColor: alpha(theme.palette.warning.light, 0.15) }
                      })
                    }}
                  >
                    <TableCell sx={{ padding: '4px 8px', textAlign: 'center' }}>{index + 1}</TableCell>
                    {type === 'original' ? (
                      <TableCell>{address.originalAddress}</TableCell>
                    ) : (
                      <>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {address.originalAddress}
                            {isNormalizedDone && isMissingInfo && (
                              <Tooltip title={`Thiếu thông tin: ${missingFields.join(', ')}`}>
                                <WarningIcon 
                                  fontSize="small" 
                                  sx={{ 
                                    color: theme.palette.warning.main,
                                    ml: 1
                                  }} 
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {address.city || (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {address.district || (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {address.commune || (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}>
                          {address.newCity || (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="info" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}>
                          {address.newCommune || (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="info" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {(address.city && address.district) ? `${address.city} - ${address.district}` : (
                            <Chip 
                              label="Trống" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ height: '20px' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', minWidth: 100 }}>
                            <Tooltip title="Sửa">
                              <IconButton 
                                size="small"
                                onClick={() => handleEdit(index, address)}
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Retry chuẩn hóa">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleRetry(index, address)}
                                  sx={{ color: theme.palette.secondary.main, ml: 1 }}
                                  disabled={retryLoading[index]}
                                >
                                  {retryLoading[index] ? <CircularProgress size={18} /> : <ReplayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Snackbar
          open={copyNotification.open}
          autoHideDuration={3000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
            {copyNotification.message}
          </Alert>
        </Snackbar>
        {selectedAddress && (
          <EditDialog
            open={editDialogOpen}
            address={selectedAddress.address}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedAddress(null);
            }}
            onSave={handleSaveEdit}
          />
        )}
      </Box>
    );
  }
);

AddressTable.displayName = 'AddressTable';

export default AddressTable; 